/**
 * CompareProgressDivider — the Compare-to-Thrive modal header divider (follow-up
 * to subtask 06).
 *
 * Replaces the modal header's plain `border-b border-border` with a thin
 * (hairline), full-modal-width, two-toned line: the already-exported fraction in
 * the `success` token, the remainder in the neutral `border` token, with a
 * centered legend ("N of M already added · X pending") overlaid on the line
 * (fieldset-legend / divider-with-text pattern). It tells the recruiter, before
 * the dry run starts, how much of the scope is already handled in Thrive.
 *
 * ZERO-REGRESSION default: when `alreadyExported === 0` (nothing exported yet —
 * true for every import before its first export) it renders EXACTLY today's
 * plain neutral hairline — no label, no color segment, no visual diff.
 *
 * Presentational: a pure function of props. It mirrors the exported/pending
 * color language already established by `ContactsStateBar` (`bg-success` +
 * proportional segments). Design-system tokens only; the sole inline style is
 * the dynamic segment width (the allowed runtime-`%` exception, same as
 * `ContactsStateBar` / `ProgressBar`).
 */

export interface CompareProgressDividerProps {
  /** Contacts in the current scope already exported to Thrive. */
  alreadyExported: number;
  /** Total contacts in the current scope (whole import, or the selection size). */
  total: number;
  /**
   * Wording of the legend: whole-import ("N of M already added") vs. a manual
   * row selection ("N of M selected already added").
   */
  scope?: "import" | "selection";
}

export function CompareProgressDivider({
  alreadyExported,
  total,
  scope = "import",
}: CompareProgressDividerProps) {
  const exported = Math.max(0, Math.min(alreadyExported, total));

  // Zero-regression: nothing exported (or an empty scope) → today's plain
  // full-width hairline, identical to the old `border-b border-border`.
  if (exported === 0 || total <= 0) {
    return (
      <div data-testid="compare-progress-divider" className="border-b border-border" />
    );
  }

  const pending = Math.max(0, total - exported);
  const pct = (n: number) => `${(n / total) * 100}%`;
  const label =
    scope === "selection"
      ? `${exported} of ${total} selected already added · ${pending} pending`
      : `${exported} of ${total} already added · ${pending} pending`;

  return (
    <div
      data-testid="compare-progress-divider"
      data-exported={exported}
      data-total={total}
      className="relative flex items-center"
    >
      {/* Two-tone hairline: exported fraction in success, remainder in border. */}
      <span className="flex h-px w-full">
        <span className="block h-full bg-success" style={{ width: pct(exported) }} />
        <span className="block h-full bg-border" style={{ width: pct(pending) }} />
      </span>
      {/* Centered legend cutting the line (fieldset-legend pattern) — the
          `bg-surface` matches the modal surface so it visually breaks the line. */}
      <span className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap bg-surface px-2 text-micro text-text-secondary">
        {label}
      </span>
    </div>
  );
}
